import React from "react";
import "./highlights.css";
import { FaList } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// Product Card Component
const ProductCard = ({ product, onFeaturedClick }) => {
  const navigate = useNavigate();
  const {
    title,
    sub_title,
    type,
    button_text,
    images,
    features,
    theme,
    main_img,
    sec_img,
  } = product;

  const handleProductClick = () => {
    navigate(`/ProductDetails/${product.id}`, {
      state: { productType: "highlight" },
    });
  };

  if (type === "featured") {
    return (
      <div className={`product-card-high featured-card ${theme || "light"}`}>
        <div className="product-content">
          <div className="product-info">
            <h2 className="product-title">{title}</h2>
            <p className="product-subtitle">{sub_title}</p>
            {button_text && (
              <button
                className="shop-button"
                onClick={() =>
                  onFeaturedClick(
                    product.category || product.tag,
                    product.title
                  )
                }
              >
                {button_text}
              </button>
            )}
          </div>
          <div
            onClick={() =>
              onFeaturedClick(product.category || product.tag, product.title)
            }
            className="product-image-container"
          >
            {(main_img || images?.main_img) && (
              <img
                src={main_img || images?.main_img}
                alt={title}
                className="product-image main-image"
              />
            )}
            {(sec_img || images?.sec_img) && (
              <div className="secondary-image-container">
                <img
                  src={sec_img || images?.sec_img}
                  alt={`${title} Box`}
                  className="secondary-image"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (type === "compact") {
    return (
      <div className={`product-card-high compact-card ${theme || "dark"}`}>
        <div className="product-content">
          <div
            onClick={() => handleProductClick(product)}
            className="compact-image-container"
          >
            {(main_img || images?.main_img) && (
              <img
                src={main_img || images?.main_img}
                alt={title}
                className="product-image compact-image"
              />
            )}
          </div>
          <div className="compact-info">
            <h3 className="compact-title">{title}</h3>
            <p className="compact-subtitle">{sub_title}</p>
            {features?.length > 0 && (
              <div className="product-features">
                {features.map((feature, index) => (
                  <span key={index} className="feature-tag">
                    {feature}
                  </span>
                ))}
              </div>
            )}
            {button_text && (
              <button
                className="compact-button"
                onClick={() => handleProductClick(product)}
              >
                {button_text}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Skeleton Loader Card
const SkeletonCard = ({ type = "featured" }) => {
  if (type === "featured") {
    return (
      <div className="product-card-high featured-card">
        <div className="product-content">
          <div className="product-info">
            <Skeleton height={40} width={200} />
            <Skeleton height={20} width={150} style={{ marginTop: 10 }} />
            <Skeleton height={40} width={120} style={{ marginTop: 20 }} />
          </div>
          <div className="product-image-container">
            <Skeleton height={300} width={400} />
            <div className="secondary-image-container">
              <Skeleton height={150} width={150} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-card-high compact-card">
      <div className="product-content">
        <div className="compact-image-container">
          <Skeleton height={200} width={200} />
        </div>
        <div className="compact-info">
          <Skeleton height={30} width={150} />
          <Skeleton height={20} width={120} style={{ marginTop: 10 }} />
          <div className="product-features">
            <Skeleton height={25} width={80} style={{ marginRight: 10 }} />
            <Skeleton height={25} width={80} />
          </div>
          <Skeleton height={35} width={120} style={{ marginTop: 15 }} />
        </div>
      </div>
    </div>
  );
};

// Highlighted Products Component
const HighLightedProducts = ({
  products = [],
  onFeaturedClick,
  handleShowAllProducts,
  highloader,
}) => {
  if (highloader) {
    return (
      <div className="product-highlight-container">
        <SkeletonCard type="featured" />
        <SkeletonCard type="compact" />
      </div>
    );
  }

  if (!products?.length) return null;

  // Split into types
  const featured = [...products.filter((p) => p.type === "featured")];
  const compact = [...products.filter((p) => p.type === "compact")];

  // Define pattern: true = featured, false = compact
  const pattern = [true, false, false, true, true, false, false, true];

  // Interleave according to pattern
  const interleavedProducts = [];
  let fi = 0;
  let ci = 0;
  let i = 0;

  while (fi < featured.length || ci < compact.length) {
    const isFeatured = pattern[i % pattern.length];
    if (isFeatured && fi < featured.length) {
      interleavedProducts.push(featured[fi++]);
    } else if (!isFeatured && ci < compact.length) {
      interleavedProducts.push(compact[ci++]);
    } else if (fi < featured.length) {
      interleavedProducts.push(featured[fi++]);
    } else if (ci < compact.length) {
      interleavedProducts.push(compact[ci++]);
    }
    i++;
  }

  return (
    <div className="product-highlight-container">
      <button className="close-button-back" onClick={handleShowAllProducts}>
        <span className="button-icon">
          <FaList />
        </span>
        <span className="button-text">show all</span>
      </button>

      {interleavedProducts.map((product) => (
        <ProductCard
          key={product.id}
          product={product}
          onFeaturedClick={onFeaturedClick}
        />
      ))}
    </div>
  );
};

export default HighLightedProducts;
